/* ═══════════════════════════════════════════════════════════════
   ANALOGUE CHARTS — the pure rules behind the chart list (design
   2026-09-18 §4 and §6, plan Task 11).

   A manager photographs paper discs, drops them in, and TachoParser
   reads them. These helpers turn one `analogue_charts` row into the
   words the manager reads, group a scanner run back together, decide
   what may be confirmed, corrected or read again, and size the upload
   requests. No React, no fetch, no clock.

   Two rules here are load-bearing and neither is cosmetic.

   ONE. The words never say a chart is done when it is not. A chart
   whose driver did not resolve is *held*: stored, visible, but with
   no activity rows filed and nothing in front of the engine. The
   receiver records that as `status = 'unattributed'` (api/server.py
   _ingest_analogue_chart_locked), and this list is the ONLY surface
   in the product where such a chart appears at all — so "confirmed"
   on its own would be the last word a manager ever heard about it.
   Same for a filing that did not finish ('pending', whether a confirm or
   a correction left it, or 'ingested' on a disc still waiting to be
   checked). This is the codebase's standing
   rule about never rendering the absence of evidence as the presence of
   compliance, applied to a status column.

   TWO. The upload is sized in BYTES. nginx caps a request body at 50M
   (nginx/nginx.conf), which is ten to fifteen phone photographs —
   fewer than one scanner run — and its refusal is a raw proxy error
   page, not a sentence the endpoint can write. So a run goes up in
   several requests that share one `batch_id`, and the chunking is by
   bytes because one phone photograph can be several megabytes while a
   scanned JPEG is a few hundred kilobytes.

   Every function returns '' / [] / false on rubbish and never throws:
   a row this screen does not understand must degrade to a quiet line,
   never to a blank page.
   ═══════════════════════════════════════════════════════════════ */

import { fmtDateWords } from './today.js';
import { priceWords } from './discReads.js';

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);
const str = (v) => (typeof v === 'string' ? v.trim() : '');
const lower = (v) => str(v).toLowerCase();

/* ── what may be uploaded ───────────────────────────────────── */

/** The server's per-file cap (design §4: `evidence.MAX_BYTES`, 10 MiB). */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * The byte budget for ONE upload request. nginx allows 50M; this is
 * comfortably under half of it, because the browser's multipart framing,
 * the headers and any proxy that counts differently all sit between this
 * number and the one nginx enforces — and going over does not produce the
 * endpoint's sentence, it produces the proxy's error page.
 */
export const CHUNK_MAX_BYTES = 24 * 1024 * 1024;

/**
 * And a count cap as well, because bytes alone would put four hundred
 * small scans in one request: the endpoint refuses a batch over
 * ANALOGUE_BATCH_MAX (100) outright, and a chunk this size keeps the
 * progress line moving.
 */
export const CHUNK_MAX_FILES = 25;

/**
 * What the endpoint reads: a photograph of the disc, and not a PDF.
 *
 * HEIC TOO, the iPhone's own format (and Android's HEIF). The server stores
 * a chart only in a format its imaging library can open
 * (`_analogue_readable_types` in api/server.py), and it opens HEIC because
 * src/analogue_vision.py registers pi-heif with Pillow at import. These
 * lists follow the server's: a type here that it could not open would be
 * refused only after the send, and one missing here would be refused in the
 * browser though the server reads it. A burst or a live photo is labelled a
 * sequence; the server reads its primary frame, so those go too.
 */
export const ACCEPTED_TYPES = Object.freeze([
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  'image/heic-sequence', 'image/heif-sequence',
]);

const EXT_TYPES = Object.freeze({
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
});

/**
 * The file input's `accept`: the types and their extensions. Asking for
 * HEIC lets an iPhone hand the page its own photograph rather than a JPEG
 * converted as the photo is chosen; the server reads either.
 */
export function acceptAttr() {
  return [...ACCEPTED_TYPES, ...Object.keys(EXT_TYPES).map((e) => `.${e}`)].join(',');
}

/** Bytes as words: 117 KB, 1.4 MB, 10 MB. '' on rubbish. */
export function sizeWords(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  const mb = n / (1024 * 1024);
  if (mb >= 1) {
    const rounded = Math.round(mb * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} MB`;
  }
  return `${Math.round(n / 1024)} KB`;
}

function extOf(file) {
  const name = lower(file.name);
  return name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
}

function typeOf(file) {
  const declared = lower(file.type);
  if (declared && declared !== 'application/octet-stream') return declared;
  /* A photograph straight off a phone can arrive with no type at all, so
     the extension is the fallback — the server decides from the bytes
     either way, this only saves the manager a round trip. */
  return EXT_TYPES[extOf(file)] || declared;
}

/**
 * '' when the file may go, otherwise the reason in words.
 *
 * Checked here as well as on the server because the endpoint is ALL OR
 * NOTHING PER REQUEST: one PDF in a chunk of eight photographs refuses
 * the whole chunk, and the manager would be left guessing which disc
 * was the problem.
 */
export function checkChartFile(file) {
  if (!isObj(file)) return 'Choose a photograph first.';
  const size = Number(file.size);
  if (!Number.isFinite(size) || size <= 0) return 'That file is empty.';
  if (size > MAX_FILE_BYTES) {
    return `That photograph is ${sizeWords(size)}; the limit is ${sizeWords(MAX_FILE_BYTES)}.`;
  }
  const type = typeOf(file);
  if (type === 'application/pdf') {
    /* "Choose", not "upload": this also answers a PDF picked for a refused
       disc's "Take a new photo", where a fresh upload would lose what was
       typed for the disc. */
    return 'A chart is read from a photograph of the disc, not a PDF. Photograph the disc and choose the picture instead.';
  }
  if (!ACCEPTED_TYPES.includes(type)) {
    return 'Only JPEG, PNG, WebP and HEIC photographs can be uploaded.';
  }
  return '';
}

/**
 * The files split into the requests they will be sent as, in order.
 *
 * Sized by bytes (see the header): a chunk holds files while it stays
 * under the budget and under the count cap. A file that would not fit
 * beside anything still goes on its own rather than being dropped — the
 * server's own 10 MB refusal is then the honest answer, and silently
 * losing a disc is not.
 *
 * @param {Array<File|{size:number}>} files
 * @returns {Array<Array>} the groups, upload order preserved
 */
export function planUploadChunks(files, { maxBytes = CHUNK_MAX_BYTES, maxFiles = CHUNK_MAX_FILES } = {}) {
  const list = Array.isArray(files) ? files : Array.from(files || []);
  const budget = Number(maxBytes) > 0 ? Number(maxBytes) : CHUNK_MAX_BYTES;
  const count = Number(maxFiles) > 0 ? Math.floor(Number(maxFiles)) : CHUNK_MAX_FILES;
  const chunks = [];
  let current = [];
  let bytes = 0;
  for (const file of list) {
    if (!file) continue;
    const size = Number(file.size);
    const weight = Number.isFinite(size) && size > 0 ? size : 0;
    if (current.length && (current.length >= count || bytes + weight > budget)) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(file);
    bytes += weight;
  }
  if (current.length) chunks.push(current);
  return chunks;
}

/**
 * What a drag over, or a drop on, the Analogue charts page does.
 *
 * The dashed box took drops and nothing else on the page did, so a photo
 * let go a little outside it was opened by the browser in a tab of its own
 * (Zed, 2026-09-29). The page now answers every file drop:
 *   'upload' — the files go the way the box sends them
 *   'block'  — kept off the browser, but nothing is added: an upload is on
 *              its way, one disc is open for checking, or the reader is
 *              switched off for the org (design 2026-10-06), when nothing
 *              new goes up at all
 *   'pass'   — not files (dragged text, a link), which is the browser's
 *              business; or a drop the box or the rows already took, which
 *              arrives handled (defaultPrevented) and must not add twice
 *
 * @param {{types?: Iterable<string>, defaultPrevented?: boolean,
 *          uploading?: boolean, reviewing?: boolean,
 *          switchedOff?: boolean}} drag
 * @returns {'upload'|'block'|'pass'}
 */
export function pageDropAction({ types, defaultPrevented, uploading, reviewing, switchedOff } = {}) {
  if (!Array.from(types || []).includes('Files') || defaultPrevented) return 'pass';
  return uploading || reviewing || switchedOff ? 'block' : 'upload';
}

/* ── the rows the list is built from ────────────────────────── */

/**
 * The chart rows out of whatever the list endpoint answered: a bare
 * array, or an envelope carrying one. [] when it carried neither, which
 * the page renders as "couldn't load", never as "no charts".
 */
export function chartRows(payload) {
  if (Array.isArray(payload)) return payload.filter(isObj);
  if (isObj(payload) && Array.isArray(payload.charts)) return payload.charts.filter(isObj);
  return [];
}

/* ── the state, in words ────────────────────────────────────── */

/* The ingest lifecycle (`status`, written by the receiver) runs ALONGSIDE
   the upload lifecycle (`review_status`), and these two values are the
   ones that mean the job is not done whatever `review_status` says. */
const HELD_INGEST = 'unattributed';
const HALF_INGEST = 'pending';

/**
 * The ingest statuses of a disc whose confirm did not finish, as the server
 * has them (`_ANALOGUE_HALF_FILED_STATUSES` in api/server.py). At 'pending'
 * the ingest had started filing, so some, all or none of the disc's hours
 * may be on the driver's timeline. At 'ingested' it filed every hour, and
 * the confirm died before it stamped the disc confirmed.
 */
export const HALF_FILED_STATUSES = Object.freeze(['ingested', 'pending']);

/**
 * The server's own sentence for such a disc (`_ANALOGUE_HALF_FILED_WORDS`),
 * word for word, so the check screen's line and the server's refusal never
 * disagree. Hedged on purpose: nobody can tell from here how many of the
 * hours are filed.
 */
export const HALF_FILED_WORDS =
  "A confirm of this chart did not finish, and some or all of its hours may already be on the driver's timeline. Confirm it again to finish it.";

/**
 * True for a disc still waiting to be checked whose confirm did not finish.
 * The server refuses to reject it or take a new photo of it (409), because
 * its hours are, or may be, filed, and confirming it again finishes it. So
 * the check screen offers neither.
 */
export function isHalfFiled(chart) {
  return canReview(chart) && HALF_FILED_STATUSES.includes(lower(chart.status));
}

/* Not done, and the list must say so: 'pending' whatever the review status
   says, and 'ingested' while the disc still waits to be checked. 'ingested'
   is also a filed chart, once its confirm is stamped, and a chart that came
   over the DDIR webhook, which has no review status at all. */
function filingUnfinished(chart) {
  return lower(chart.status) === HALF_INGEST || isHalfFiled(chart);
}

/* The door a held or unfinished disc goes back through, as the verb the
   words use. Confirm for a disc waiting to be checked, which is where a held
   confirm or correction sends it; the check screen's outcome line asks with
   a row that has no review status, and means that disc. Correct once the
   disc is confirmed: Confirm refuses it (409), so "confirm it again" sent
   the manager to a door that is shut, and a disc a correction left part way
   is finished only by correcting it again (`needsFinishing`). */
function againVerb(chart) {
  return lower(chart.review_status) === 'confirmed' ? 'correct' : 'confirm';
}

function reasonOf(chart) {
  return str(chart.extraction_error) || str(chart.rejection_reason);
}

/** The chart's own line: "6 Sep, MX07 KGA, TESTER A", as much as is known. */
function chartLine(chart) {
  /* driver_name_resolved ONLY. `driver_name_written` is the handwriting
     the model read, and a handwritten name never stands in for a driver's
     identity in this codebase — showing it here would read as the driver
     the hours were filed against. */
  return [
    fmtDateWords(chart.chart_date_start),
    str(chart.vehicle_registration),
    str(chart.driver_name_resolved),
  ].filter(Boolean).join(', ');
}

/**
 * The state of one chart in words a transport manager reads, not a
 * status code (design §6).
 *
 *   queued           Waiting to be read
 *   extracting       Reading…
 *   needs_review     Ready to check
 *   rejected_quality Couldn’t read it — <the reason, a sentence>
 *   failed           Something went wrong reading it — <the reason>
 *   confirmed        Confirmed — 6 Sep, MX07 KGA, TESTER A
 *   discarded        Rejected — <the reason>
 *
 * A chart that did not attach to a driver says THAT, ahead of everything
 * else, because nothing of it has reached the timeline and this list is
 * the only place it is visible. So does one whose filing did not finish
 * ("Filing did not finish — confirm it again", or "— correct it again" for
 * a confirmed disc a correction left part way). '' on rubbish.
 */
export function chartStateWords(chart) {
  if (!isObj(chart)) return '';
  const review = lower(chart.review_status);
  const ingest = lower(chart.status);

  if (ingest === 'withdrawn') {
    const why = str(chart.withdrawn_reason);
    return why ? `Withdrawn — ${why}` : 'Withdrawn — its hours are off the timeline';
  }
  if (ingest === HELD_INGEST) {
    /* Held, whether the confirm stamped `confirmed` or left the row at
       `needs_review`: the chart is stored, no activity rows were filed,
       and no later DDIR event can attach a TP-picked chart (plan Task 9).
       The manager fixes the roster and confirms again. */
    return 'No driver matched — nothing has been filed';
  }
  if (filingUnfinished(chart)) {
    return `Filing did not finish — ${againVerb(chart)} it again`;
  }

  switch (review) {
    case 'queued':
      return 'Waiting to be read';
    case 'extracting':
      return 'Reading…';
    case 'needs_review':
      return 'Ready to check';
    case 'rejected_quality': {
      const why = reasonOf(chart);
      return why ? `Couldn’t read it — ${why}` : 'Couldn’t read the disc';
    }
    case 'failed': {
      const why = reasonOf(chart);
      return why ? `Something went wrong reading it — ${why}` : 'Something went wrong reading it';
    }
    case 'confirmed': {
      const line = chartLine(chart);
      return line ? `Confirmed — ${line}` : 'Confirmed';
    }
    case 'discarded': {
      const why = str(chart.rejection_reason);
      return why ? `Rejected — ${why}` : 'Rejected';
    }
    case '': {
      /* No upload lifecycle at all: a chart that came in over the DDIR
         webhook, which was extracted and confirmed before TachoParser
         ever saw it. It still belongs in this list — it is one of the
         org's charts — so it is named from the ingest side. */
      if (ingest === 'ingested') {
        const line = chartLine(chart);
        return line ? `Confirmed — ${line}` : 'Confirmed';
      }
      return '';
    }
    default:
      return 'State not recognised';
  }
}

/**
 * The state as a tone, for the badge: 'reading' | 'ready' | 'done' |
 * 'held' | 'problem' | 'gone' | ''. Held and problem are deliberately
 * NOT the same as done.
 */
export function chartStateTone(chart) {
  if (!isObj(chart)) return '';
  const review = lower(chart.review_status);
  const ingest = lower(chart.status);
  if (ingest === 'withdrawn') return 'gone';
  if (ingest === HELD_INGEST || filingUnfinished(chart)) return 'held';
  switch (review) {
    case 'queued':
    case 'extracting':
      return 'reading';
    case 'needs_review':
      return 'ready';
    case 'rejected_quality':
    case 'failed':
      return 'problem';
    case 'confirmed':
      return 'done';
    case 'discarded':
      return 'gone';
    case '':
      return ingest === 'ingested' ? 'done' : '';
    default:
      return '';
  }
}

/**
 * What to do about a chart that is not done, in one sentence; '' when
 * there is nothing to do. The list says what happened; this says what
 * happens next, which for a held chart is the only route out.
 *
 * `canReadAgain` is false where the screen offers no way to read the disc
 * again: with the reader switched off for the org (design 2026-10-06),
 * Read it again and Take a new photo, which spend a model call, are not
 * there. A hint never names a button that is not there, so a disc that
 * could not be read is then told only what is still true: nothing was
 * filed for it.
 */
export function chartHint(chart, { canReadAgain = true } = {}) {
  if (!isObj(chart)) return '';
  const review = lower(chart.review_status);
  const ingest = lower(chart.status);
  /* Confirm again, or correct again for a disc that is confirmed already
     (againVerb): the one door that takes it. */
  if (ingest === HELD_INGEST) {
    return `Add the driver to your roster, then open this chart and ${againVerb(chart)} it again.`;
  }
  /* Hedged as the server's sentence is (HALF_FILED_WORDS): the hours may be
     filed already, in part or in full, and filing it again finishes it. */
  if (filingUnfinished(chart)) {
    return "Some or all of its hours may already be on the driver's timeline. "
      + `Open this chart and ${againVerb(chart)} it again to finish it.`;
  }
  /* A disc that could not be read, where nothing here can read it again:
     what is still true, and no button named. */
  if ((review === 'rejected_quality' || review === 'failed') && !canReadAgain) {
    return 'Nothing has been filed for it.';
  }
  /* A disc that could not be read is pointed at the row's "Take a new
     photo" button, never at a fresh upload: a fresh upload starts a new
     disc and loses what was typed for this one, and the button keeps it.
     The shot is described by what to look for before it, as the drop zone
     does, because a disc the frame cuts is turned away again. */
  if (review === 'rejected_quality') {
    return 'Take a new photo of the disc, square on, with some desk visible all round it. You do not need to type it again.';
  }
  /* A failed read may be the model's fault rather than the photo's, so
     both ways out are offered, by the buttons' own labels. */
  if (review === 'failed') {
    return 'Nothing has been filed for it. Press Read it again, or Take a new photo.';
  }
  return '';
}

/** True while TachoParser is still reading this chart. */
export function isBeingRead(chart) {
  if (!isObj(chart)) return false;
  const review = lower(chart.review_status);
  return review === 'queued' || review === 'extracting';
}

/**
 * How long a screen waits between reads while TachoParser is reading a disc,
 * in milliseconds: the list while any of its discs is being read or read
 * again (`anyBeingRead`), and a disc's page while its new read is on its way
 * (`rereadStage` 'reading'). One interval for both, so a disc never moves on
 * one screen and sits still on the other.
 */
export const READ_POLL_MS = 4000;

/**
 * True while ANY chart is still being read — the poll gate. A list that
 * polls when nothing is happening heats a laptop for nothing, so the page
 * stops the timer the moment this goes false.
 *
 * A filed disc being READ AGAIN counts too (`rereadStage` 'reading'). Its
 * review status stays 'confirmed' throughout, so without its re-read the
 * timer would stop while the new read was still on its way, and "Reading
 * again…" would never become "New read ready to compare" without a reload.
 */
export function anyBeingRead(charts) {
  const rows = chartRows(charts);
  return rows.some((chart) => isBeingRead(chart) || rereadStage(chart) === 'reading');
}

/** Only a chart that could not be read may be asked for again (plan Task 9:
    a retry is never automatic, because one that silently costs money is a
    surprise). */
export function canRetry(chart) {
  if (!isObj(chart)) return false;
  const review = lower(chart.review_status);
  return review === 'failed' || review === 'rejected_quality';
}

/** A chart the manager can open and check. */
export function canReview(chart) {
  if (!isObj(chart)) return false;
  return lower(chart.review_status) === 'needs_review';
}

/* ── correcting a filed disc (design 2026-09-30 B) ──────────── */

/**
 * The ingest statuses of a disc the Correct door takes, as the server has
 * them (`_ANALOGUE_CORRECTABLE_STATUSES` in api/server.py): filed
 * ('ingested'), or left by a correction that did not finish. That leaves the
 * disc confirmed over whatever its ingest got to: 'pending' (some, all or
 * none of its hours filed) or 'unattributed' (its driver did not attach, and
 * the stamp that sends a held disc back to be checked did not land). Confirm
 * and Reject both refuse a confirmed disc, so correcting it again is the only
 * way to finish it.
 */
export const CORRECTABLE_STATUSES = Object.freeze(['ingested', 'pending', 'unattributed']);

/**
 * True for a disc the Correct door takes, by that door's own rule
 * (`_analogue_is_correctable`, which the one-chart read answers as
 * `can_correct`): uploaded in TP, confirmed, and filed or left part way by a
 * correction. A disc sent from DDIR is corrected in DDIR, which owns its
 * versions, and a disc still waiting to be checked is Confirm's.
 */
export function canCorrect(chart) {
  if (!isObj(chart)) return false;
  return lower(chart.origin) === 'upload'
    && lower(chart.review_status) === 'confirmed'
    && CORRECTABLE_STATUSES.includes(lower(chart.status));
}

/**
 * The mark the server puts on a correction's entry from before it files the
 * disc until the correction is stamped (`_ANALOGUE_CORRECTION_FILING` in
 * api/server.py): an entry still marked is a correction that did not finish.
 * Matched exactly, as the server matches it.
 */
export const CORRECTION_FILING = 'filing';

/* Whether the disc holds an entry a correction left 'filing' that the
   Correct door finishes: any but one for a version the disc never reached (a
   whole number above the disc's own, as the server's
   `_analogue_correction_unfiled` judges it), which filed nothing and which
   the server drops. The disc's own version is 0 when it has none, as the
   server reads it. */
function hasUnfinishedCorrection(chart) {
  const filed = Number.isInteger(chart.version) ? chart.version : 0;
  const entries = Array.isArray(chart.corrections) ? chart.corrections : [];
  return entries.some((entry) => isObj(entry) && entry.state === CORRECTION_FILING
    && !(Number.isInteger(entry.version) && entry.version > filed));
}

/**
 * True for a correctable disc that a correction left part way: correcting it
 * finishes it, and the door takes it with no box changed (the reason is
 * still required). Read as the Correct door reads it (its `finishing`): the
 * disc's ingest left it short of filed, or its last correction's entry is
 * still 'filing' (a stamp lost after the ingest filed the disc), which the
 * one-chart read's `corrections` and `version` say. A list row carries no
 * corrections, and is read on its status alone.
 */
export function needsFinishing(chart) {
  return canCorrect(chart) && (lower(chart.status) !== 'ingested' || hasUnfinishedCorrection(chart));
}

/* ── reading a filed disc again (design 2026-10-01) ─────────── */

/**
 * The statuses at which a re-read of a filed disc is OPEN, as the server has
 * them (`_ANALOGUE_REREAD_OPEN` in api/server.py): waiting for the reader,
 * being read, or read and waiting for the manager to compare it. A disc holds
 * one open re-read at most, so a disc with one is never offered another: the
 * second ask could only be refused.
 */
export const REREAD_OPEN_STATUSES = Object.freeze(['queued', 'extracting', 'ready']);

/**
 * What a manager is asked before a disc is read again, on the list and on
 * the disc page (design 2026-10-01): nothing filed changes until the two
 * traces have been compared. Still asked first, because the read costs
 * TachoParser a model call, but it costs the customer nothing: Zed's pricing
 * (2026-10-02) is £1 a disc read, with re-reads and failed reads free, so the
 * sentence says so rather than warn of a charge.
 */
export const REREAD_CONFIRM_WORDS = 'Read this disc again with the current reader? There is no charge for a re-read. '
  + 'The filed record stays as it is until you compare the two and choose.';

/**
 * ... and before it is read again from a NEW photo sent with the request
 * (Zed, 2026-10-20): the same, and what filing the new read does to the
 * photo, since that is the one thing a new-photo re-read changes beyond the
 * trace (api/server.py, _analogue_stamp_reread_filed).
 */
export const REREAD_PHOTO_CONFIRM_WORDS = 'Read this disc again from the new photo? There is no charge for a re-read. '
  + 'The filed record and its photo stay as they are until you compare the two and choose; '
  + 'filing the new read makes the new photo the disc’s.';

/**
 * The request door's sentence for a disc a filing left part way, as the
 * server has it (`_ANALOGUE_REREAD_PART_WAY_WORDS` in api/server.py), word
 * for word: the disc page says it where Read again would be. "A filing", for
 * the filing left part way may be a correction's or a new read's.
 */
export const REREAD_PART_WAY_WORDS =
  'A filing of this disc did not finish. Correct the disc to finish it, then read it again.';

/**
 * File's own sentence for a disc a filing left part way, a correction's or a
 * new read's (`_ANALOGUE_REREAD_FILE_PART_WAY_WORDS`), word for word: the
 * disc page says it where File would be. A new read filed over half a filing
 * would bury what that filing left, so the disc is corrected first.
 */
export const REREAD_FILE_PART_WAY_WORDS =
  'A filing of this disc did not finish. Correct the disc to finish it, then file the new read.';

/**
 * What the disc page says in place of File and Keep when filing THIS new read
 * was left part way (`rereadLeftPartWay`): its hours may already be on the
 * timeline, and correcting the disc finishes the filing, the new read's
 * included. Both doors refuse such a read (409) in this same sentence, the
 * server's `_ANALOGUE_REREAD_LEFT_FILING_WORDS`, which a page gone stale
 * shows as written; analogueCharts.test.mjs pins the two together.
 */
export const REREAD_LEFT_PART_WAY_WORDS = "Filing this new read did not finish, so its hours may "
  + "already be on the driver's timeline. Correct the disc to finish it.";

/**
 * True for a disc the request door reads again (`POST /reread`), by that
 * door's own rule (`_analogue_is_rereadable`, and no re-read open): uploaded
 * in TP, so TP holds the photograph to read (a disc from DDIR has its images
 * there); confirmed; filed in full ('ingested'); no correction left part way
 * (`needsFinishing`), because a new read filed over half a filing would bury
 * what that filing left, and correcting the disc finishes it first; and no
 * re-read on its way or ready to compare. A re-read the reader could not use
 * (failed, rejected_quality) does not stand in the way: asking again closes
 * it.
 *
 * THE SERVER'S ANSWER WINS. The one-chart read answers `can_reread` itself,
 * over what a list row does not carry (every correction entry, and the
 * re-read open at that moment), so on the disc page a `can_reread` that is
 * true or false is the answer, and anything else is not one. The rule below
 * is the list's fallback, where a row's `reread_status` (or the page's
 * `reread`) says where its re-read is.
 */
export function canReread(chart) {
  if (!isObj(chart)) return false;
  if (typeof chart.can_reread === 'boolean') return chart.can_reread;
  return canCorrect(chart)
    && lower(chart.status) === 'ingested'
    && !needsFinishing(chart)
    && !REREAD_OPEN_STATUSES.includes(lower(chart.reread_status ?? chart.reread?.status));
}

/**
 * True for a disc read by an older reader than the current one: the row's
 * `prompt_version` (the reader that read it) below `current` (the list's or
 * the one-chart read's `current_prompt_version`). The list nudges such a
 * disc: a sharper read is available. Only whole numbers are readers, so a
 * disc whose reader was never recorded is never called older, and nothing
 * is, against a current reader that is not known.
 */
export function readByOlderReader(chart, current) {
  return isObj(chart)
    && Number.isInteger(chart.prompt_version)
    && Number.isInteger(current)
    && chart.prompt_version < current;
}

/* A shown re-read's status in the manager's words. Shown is open, or one the
   reader could not use that nobody has closed yet (`_ANALOGUE_REREAD_SHOWN`
   in api/server.py). */
const REREAD_WORDS = Object.freeze({
  queued: 'Reading again…',
  extracting: 'Reading again…',
  ready: 'New read ready to compare',
  rejected_quality: 'The new read could not use this photograph',
  failed: 'The new read failed',
});

/**
 * Where a filed disc's re-read is, in words, for the list's chip and the disc
 * page: 'Reading again…' while it waits for the reader or is being read, 'New
 * read ready to compare', 'The new read could not use this photograph' or
 * 'The new read failed'. null for anything else: a filed or kept re-read is
 * history, which the disc's own words and its history say, and a status this
 * screen does not know says nothing rather than something made up.
 */
export function rereadStatusWords(status) {
  const key = lower(status);
  return Object.prototype.hasOwnProperty.call(REREAD_WORDS, key) ? REREAD_WORDS[key] : null;
}

/* A shown re-read's status as the stage the screens branch on. */
const REREAD_STAGES = Object.freeze({
  queued: 'reading',
  extracting: 'reading',
  ready: 'ready',
  rejected_quality: 'unusable',
  failed: 'unusable',
});

/**
 * Where a disc's re-read is, as the one word the list and the disc page
 * branch on: 'reading' while it waits for the reader or is being read,
 * 'ready' once it is read and waiting to be compared with the filed trace,
 * 'unusable' when the reader could not use it (failed, rejected_quality),
 * and '' for none. A filed or kept re-read is history, and a status this
 * screen does not know is none rather than something made up. Read as
 * `canReread` reads it, off a list row's `reread_status` or the disc page's
 * `reread`, so neither screen reads a status itself.
 */
export function rereadStage(chart) {
  if (!isObj(chart)) return '';
  const key = lower(chart.reread_status ?? chart.reread?.status);
  return Object.prototype.hasOwnProperty.call(REREAD_STAGES, key) ? REREAD_STAGES[key] : '';
}

/**
 * True when filing the disc page's new read was left part way: the one-chart
 * read's `reread.part_way`, which the server answers for THIS read (its
 * filing committed the disc's next version and did not finish). Its hours may
 * already be on the driver's timeline, so it is neither filed again nor kept:
 * correcting the disc finishes it. Only the server's true is part way; a view
 * without the flag, or with anything else in it, is not.
 */
export function rereadLeftPartWay(chart) {
  return isObj(chart) && isObj(chart.reread) && chart.reread.part_way === true;
}

/* ── the scanner run ────────────────────────────────────────── */

/**
 * The charts grouped as they were uploaded: one group per `batch_id`,
 * newest run first, and within a run the order the files went up.
 *
 * A run reaches the server as several requests sharing one batch id, so
 * the group is what the manager thinks of as "the discs I put in this
 * morning" — and a run whose upload stopped half way is still one group,
 * short a few discs, which is exactly what it was.
 *
 * A chart with no batch id (one that came over the DDIR webhook) stands
 * on its own rather than being swept into a shared "no batch" pile: those
 * charts did not arrive together and saying they did would be untrue.
 *
 * @returns {Array<{key: string, batch_id: string, received_at: string, charts: Array}>}
 */
export function groupByBatch(charts) {
  const rows = chartRows(charts);
  const groups = [];
  const byKey = new Map();
  rows.forEach((chart, index) => {
    const batchId = str(chart.batch_id);
    const key = batchId || `chart:${str(chart.chart_id) || index}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, batch_id: batchId, received_at: '', charts: [], seen: index };
      byKey.set(key, group);
      groups.push(group);
    }
    group.charts.push({ chart, index });
    const at = str(chart.received_at);
    /* The group's own time is its NEWEST row: a run uploaded in chunks
       carries one timestamp per chunk, and the run belongs where its last
       chunk put it. */
    if (at > group.received_at) group.received_at = at;
  });
  groups.sort((a, b) => (
    b.received_at.localeCompare(a.received_at) || a.seen - b.seen
  ));
  return groups.map(({ key, batch_id: batchId, received_at: receivedAt, charts: items }) => ({
    key,
    batch_id: batchId,
    received_at: receivedAt,
    /* Upload order within the run: oldest chunk first, and inside one
       chunk (every row of which shares a timestamp to the microsecond)
       the order the server listed them, which is the order they were
       written. */
    charts: items
      .slice()
      .sort((a, b) => (
        str(a.chart.received_at).localeCompare(str(b.chart.received_at)) || a.index - b.index
      ))
      .map(({ chart }) => chart),
  }));
}

/** "12 discs" / "1 disc" / '' for an empty group. */
export function batchCountWords(group) {
  const n = isObj(group) && Array.isArray(group.charts) ? group.charts.length : 0;
  if (!n) return '';
  return `${n} disc${n === 1 ? '' : 's'}`;
}

/* ── what a disc read costs (design 2026-10-06) ─────────────── */

/**
 * The list's `billing`, {price_pence, discs_this_month}, in the words the
 * page shows: { priceLine, countLine }, each '' when there is nothing true
 * to say.
 *
 *   priceLine  '£1 per disc read · re-reads and failed reads are free',
 *              beside the drop zone. The price is the one DDIR stored for
 *              the org, a display copy (the binding figure is on DDIR's
 *              invoice), in lib/discReads' pounds. '' when the answer gives
 *              none, as it does until DDIR stores one.
 *   countLine  '3 discs read this month' ('1 disc'), in the counts card:
 *              the discs first read this UK month, by the rule DDIR bills
 *              on. '' when the answer gives none, as it does while the
 *              server cannot count them truly.
 *
 * A price is whole pence above 0. None, 0, or anything else says nothing
 * about money: a client is never told a disc read costs '£0', nor shown a
 * made-up price. And the count is said only beside a price, as a whole
 * number of discs, 0 or more: nothing changes for a client until DDIR sends
 * a price (design 2026-10-06), and a short count, or 'NaN', is worse than
 * no line at all.
 */
export function billingWords(billing) {
  const given = isObj(billing) ? billing : {};
  const pence = given.price_pence;
  const discs = given.discs_this_month;
  const price = Number.isInteger(pence) && pence > 0 ? priceWords(pence) : '';
  const priceLine = price ? `${price} per disc read · re-reads and failed reads are free` : '';
  return {
    priceLine,
    countLine: priceLine && Number.isInteger(discs) && discs >= 0
      ? `${discs} disc${discs === 1 ? '' : 's'} read this month`
      : '',
  };
}

/* ── the review screen's gate (used by Task 12) ─────────────── */

/**
 * Whether a chart's corrected fields may be confirmed: it needs a driver
 * and a registration. Both are identity — the hours land on that driver's
 * record and that truck's — and neither may be guessed from the
 * handwriting.
 */
export function canConfirm(fields) {
  if (!isObj(fields)) return false;
  return Boolean(str(fields.driver_id) && str(fields.vehicle_registration));
}
